import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { api } from '../services/api';

type ResultadoItem = {
  nome_cliente: string;
  cliente_id: number;
  contrato: string;
  servico: string;
};

interface Props {
  open: boolean;
  onClose: () => void;
}

const CgnatPesquisarClienteDialog: React.FC<Props> = ({ open, onClose }) => {
  const [ipPublico, setIpPublico] = useState('');
  const [porta, setPorta] = useState<string>('');
  const [dataHora, setDataHora] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultados, setResultados] = useState<ResultadoItem[]>([]);

  useEffect(() => {
    if (!open) {
      // reset ao fechar
      setIpPublico('');
      setPorta('');
      setDataHora('');
      setError(null);
      setResultados([]);
      setLoading(false);
    }
  }, [open]);

  const handleConsultar = async () => {
    setError(null);
    setResultados([]);

    if (!ipPublico.trim()) {
      setError('Informe o IP público.');
      return;
    }
    if (!porta || Number.isNaN(Number(porta))) {
      setError('Informe a porta (número).');
      return;
    }
    if (!dataHora) {
      setError('Informe a data e hora.');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        ip_publico: ipPublico.trim(),
        porta: Number(porta),
        data_hora: new Date(dataHora).toISOString(),
      };
      const response: any = await api.post('/cgnat/pesquisar-cliente', payload);
      if (response && response.success) {
        setResultados(response.data || []);
      } else {
        setResultados([]);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Erro ao consultar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SearchIcon />
          Pesquisar Cliente (CGNAT)
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              label="IP Público"
              value={ipPublico}
              onChange={(e) => setIpPublico(e.target.value)}
              placeholder="Ex: 200.2.3.10"
              size="small"
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              label="Porta"
              value={porta}
              onChange={(e) => setPorta(e.target.value)}
              placeholder="Ex: 53000"
              size="small"
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              label="Data e Hora"
              type="datetime-local"
              value={dataHora}
              onChange={(e) => setDataHora(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2 }}>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nome Cliente</TableCell>
                  <TableCell>Contrato</TableCell>
                  <TableCell>Serviço</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {resultados.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      {loading ? 'Consultando...' : 'Sem resultados'}
                    </TableCell>
                  </TableRow>
                ) : (
                  resultados.map((r, idx) => (
                    <TableRow key={idx}>
                      <TableCell><Link href={`/clients/${r.cliente_id}`}>{r.nome_cliente}</Link></TableCell>
                      <TableCell>{r.contrato}</TableCell>
                      <TableCell>{r.servico}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" disabled={loading}>Fechar</Button>
        <Button onClick={handleConsultar} variant="contained" startIcon={loading ? <CircularProgress size={18} /> : <SearchIcon />} disabled={loading}>
          {loading ? 'Consultando...' : 'Consultar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CgnatPesquisarClienteDialog;


